'use client';

import { c as _c } from "react/compiler-runtime";
import type { UserPortfolioSnapshotWire } from '@morfi/backend-sdk-v2/client';
import { createSubscriberQueryFn, useQuery, useQueryClient } from '@morfi/backend-sdk-v2/client';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import type { OnchainTradeData } from '@/queries/onchain-trade-data';
import { CONFIRMED_POSITION_META, type ConfirmedPositionTransition, confirmedPositionQueryKey, EMPTY_TRANSITIONS } from './confirmed-position-transitions';
import type { PositionCardRow } from './display-types';
import { useUserPortfolioSnapshotQuerySelect } from './query-select';
import { userPortfolioPositionDisplayRow } from './valuation';
const subscriber: typeof createSubscriberQueryFn<readonly ConfirmedPositionTransition[]> = createSubscriberQueryFn<readonly ConfirmedPositionTransition[]>;
export function useConfirmedPositionRows(args) {
  const $ = _c(4);
  const {
    transitions,
    snapshot
  } = useConfirmedPositionTransitions(args.userId);
  let t0;
  if ($[0] !== args || $[1] !== snapshot || $[2] !== transitions) {
    t0 = confirmedPositionRows({
      ...args,
      transitions,
      snapshot
    });
    $[0] = args;
    $[1] = snapshot;
    $[2] = transitions;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
export function useConfirmedPositionTransitions(userId) {
  const $ = _c(11);
  const client = useQueryClient();
  const network = useActiveNetwork();
  let key;
  let t0;
  if ($[0] !== client || $[1] !== network || $[2] !== userId) {
    key = confirmedPositionQueryKey(userId, network);
    t0 = subscriber(client, key);
    $[0] = client;
    $[1] = network;
    $[2] = userId;
    $[3] = key;
    $[4] = t0;
  } else {
    key = $[3];
    t0 = $[4];
  }
  const queryFn = t0;
  let t1;
  if ($[5] !== key || $[6] !== queryFn) {
    t1 = {
      queryKey: key,
      queryFn,
      enabled: false,
      meta: CONFIRMED_POSITION_META
    };
    $[5] = key;
    $[6] = queryFn;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const query = useQuery(t1);
  const snapshot = useUserPortfolioSnapshotQuerySelect(userId, selectSnapshot).data ?? null;
  const t2 = query.data ?? EMPTY_TRANSITIONS;
  let t3;
  if ($[8] !== snapshot || $[9] !== t2) {
    t3 = {
      transitions: t2,
      snapshot
    };
    $[8] = snapshot;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
export function confirmedPositionRows({
  rows,
  transitions,
  snapshot,
  wallet,
  market,
  outcomeMint,
  tradeData
}: {
  readonly rows: readonly PositionCardRow[];
  readonly transitions: readonly ConfirmedPositionTransition[];
  readonly snapshot: UserPortfolioSnapshotWire | null;
  readonly wallet: string;
  readonly market?: string;
  readonly outcomeMint?: string;
  readonly tradeData?: OnchainTradeData;
}): readonly PositionCardRow[] {
  if (snapshot === null || transitions.length === 0) return rows;
  let next = rows;
  for (const {
    row
  } of transitions) {
    if (row.wallet !== wallet || market !== undefined && row.market !== market || outcomeMint !== undefined && row.outcomeMint !== outcomeMint) continue;
    const authoritative = snapshot.positions.find(position => position.wallet === row.wallet && position.outcomeMint === row.outcomeMint);
    if ((authoritative?.tradeCount ?? 0) >= row.tradeCount || authoritative !== undefined && authoritative.lifecycle !== row.lifecycle) continue;
    const poolEntry = tradeData?.market === row.market ? {
      status: 'complete' as const,
      snapshot: tradeData,
      identity: {
        poolId: null,
        outcomeMint: null,
        ammConfigAddress: null
      },
      coordinates: row.coordinates
    } : snapshot.poolSnapshotsByMarket[row.market];
    const display = userPortfolioPositionDisplayRow(row, poolEntry);
    next = [...next.filter(position => position.wallet !== row.wallet || position.outcomeMint !== row.outcomeMint), display];
  }
  return next;
}
function selectSnapshot(snapshot: UserPortfolioSnapshotWire | null): UserPortfolioSnapshotWire | null {
  return snapshot;
}